import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { NotificationProvider } from "@/providers/NotificationProvider";
import NotificationManager from "@/services/NotificationManager";

const queryClient = new QueryClient();

const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const ChurchAdmin = lazy(() => import("./pages/ChurchAdmin"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ShareInvite = lazy(() => import("./pages/ShareInvite"));
const Landing = lazy(() => import("./pages/Landing"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ChallengeShare = lazy(() => import("./pages/ChallengeShare"));
const Support = lazy(() => import("./pages/Support"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Developers = lazy(() => import("./pages/Developers"));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const RouteLogger = ({ name, children }: { name: string; children: any }) => {
  console.log(`[BOOT] Attempting to render route: ${name}`);
  try {
    const element = <>{children}</>;
    console.log(`[BOOT] Successfully rendered route: ${name}`);
    return element;
  } catch (err) {
    console.error(`[BOOT] Route failed: ${name}`, err);
    return <div style={{ padding: 24, color: 'red' }}>Error rendering {name}</div>;
  }
};


const AppRoutes = () => {
  try {
    console.log('[BOOT] Rendering routes...');
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<RouteLogger name="/"><Landing /></RouteLogger>} />
          <Route path="/app" element={<RouteLogger name="/app"><Index /></RouteLogger>} />
          <Route path="/auth" element={<RouteLogger name="/auth"><Auth /></RouteLogger>} />
          <Route path="/reset-password" element={<RouteLogger name="/reset-password"><ResetPassword /></RouteLogger>} />
          <Route path="/church-admin" element={<RouteLogger name="/church-admin"><ChurchAdmin /></RouteLogger>} />
          <Route path="/share" element={<RouteLogger name="/share"><ShareInvite /></RouteLogger>} />
          <Route path="/challenge/:slug" element={<RouteLogger name="/challenge/:slug"><ChallengeShare /></RouteLogger>} />
          <Route path="/support" element={<RouteLogger name="/support"><Support /></RouteLogger>} />
          <Route path="/privacy" element={<RouteLogger name="/privacy"><Privacy /></RouteLogger>} />
          <Route path="/terms" element={<RouteLogger name="/terms"><Terms /></RouteLogger>} />
          <Route path="/developers" element={<RouteLogger name="/developers"><Developers /></RouteLogger>} />
          <Route path="*" element={<RouteLogger name="*"><NotFound /></RouteLogger>} />
        </Routes>
      </Suspense>
    );
  } catch (err) {
    console.error('[BOOT] AppRoutes render failed', err);
    return <div style={{ padding: 24, color: 'red' }}>AppRoutes render error</div>;
  }
};

const AppShell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user?.id) {
      console.log("🔔 Initializing push for user:", user.id);
      NotificationManager.getInstance().registerPushHandler();
    }
  }, [user?.id]);

  useEffect(() => {
    // Only redirect if user state changes AND we're on wrong page
    // Don't redirect from landing page - let it handle its own navigation
    // Allow public access to challenge share and invite links
    // Don't include navigate in deps to avoid loops
    if (user === null && 
        location.pathname !== "/auth" && 
        location.pathname !== "/reset-password" && 
        location.pathname !== "/" &&
        location.pathname !== "/support" &&
        location.pathname !== "/privacy" &&
        location.pathname !== "/terms" &&
        !location.pathname.startsWith("/challenge/") &&
        !location.pathname.startsWith("/share")) {
      navigate("/auth", { replace: true });
    } else if (user && location.pathname === "/auth") {
      navigate("/app", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname]);

  return <AppRoutes />;
};

const App = () => {
  console.log('[BOOT] Rendering App.tsx');

  // Clear any stuck badge count on app launch (cleanup from capacitor test)
  useEffect(() => {
    (async () => {
      try {
        const { clearAllDeliveredNotifications } = await import('@/utils/notifications');
        await clearAllDeliveredNotifications();
      } catch (e) {
        // silently ignore
      }
    })();
  }, []);

  // One-time native haptics pre-warm to avoid first-use 3s freeze
  useEffect(() => {
    let timer: number | undefined;
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        
        // Defer to idle: small delay avoids competing with boot work
        timer = window.setTimeout(async () => {
          try {
            const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
            // Very light warmup to initialize the native bridge
            await Haptics.impact({ style: ImpactStyle.Light });
          } catch (e) {
            // silently ignore; warmup is best-effort
          }
        }, 1200);
      } catch {
        // silently ignore if Capacitor not available
      }
    })();
    return () => { if (timer) clearTimeout(timer); };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <NotificationProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <AppShell />
          </TooltipProvider>
        </NotificationProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
